import { createContext, Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import capitalize from 'lodash/capitalize';

import { bugsnag } from 'shared/utils/services/bugsnag';
import { fetchProductIdData } from 'productTemplate/utils/fetchProductIdData';
import { fetchUserProductIdOffer } from 'productTemplate/utils/fetchUserProductIdOffer';
import { IAuctionOffer } from 'shared/types/AuctionOffer';
import { isSpecialType } from 'productTemplate/utils/isSpecialType';
import { ITopOfferData } from 'productTemplate/types/TopOfferData';
import { IUserProductIdOfferResponse } from 'productTemplate/types/UserProductIdOfferResponse';
import { IVariantData } from 'productTemplate/components/buyBar/VariantDetail';
import { IWebOffer } from 'shared/types/WebOffer';
import { requestService } from 'shared/utils/services/requestService';
import { SIZE_MAP } from 'shared/enums/Size';
import { useLocation } from 'shared/hooks/useLocation';
import { useLogInBeforeAction } from 'productTemplate/hooks/useLogInBeforeAction';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { t } from 'localization/utils/t';
import { localizeDate } from 'shared/utils/localizeDate';
import { OFFERS_LOGIN_DARWIN_TEST } from 'productTemplate/enums/offersLoginDarwinTest';

const TP = 'productTemplate.providers.OffersProvider';

interface IOffersProvider {
  children: ReactNode;
}

export type IGetTopOfferBySize = (size: number) => ITopOfferData;

export interface IOffersContext {
  buyNowAmount: string;
  buyNowAmountUsd: string;
  buyNowCheckoutLink: string;
  condition: string;
  existingOffers: IWebOffer[] | IAuctionOffer[];
  fetchUserProductIdOffer: (productId: string) => void;
  fetchTopOffers: () => Promise<void>;
  getExistingOffer: (sizeValue: number) => IWebOffer | IAuctionOffer;
  getExistingConditionalOffer?: () => IWebOffer | IAuctionOffer;
  getExistingOffers: () => Promise<void>;
  getTopOfferBySize: IGetTopOfferBySize;
  hidePlaceOfferModal: () => void;
  isOfferable: boolean;
  isLoading: boolean;
  isPlaceOfferModalShowing: boolean;
  lastSaleAmount: string;
  lastSaleAmountUsd: string;
  offersLoginTestGroup?: string;
  placeOfferModalSize: number;
  productVariant?: IVariantData;
  showPlaceOfferModal: (
    size: number,
    productVariant?: IVariantData,
    userProductVariantOffer?: IUserProductIdOfferResponse | IVariantData,
    options?: {
      onOpenModalCallbackFn?: () => void;
      onDeletedOfferCallbackFn?: () => void;
    },
    offersLoginExperimentGroup?: string,
  ) => void;
  sizePresentation: string;
  isSnackbarVisible?: boolean;
  setIsSnackbarVisible?: Dispatch<SetStateAction<boolean>>;
  snackbarMessage?: ReactNode;
  showSnackbarForOffer?: (newExpirationDate: Date, offerAmount: string) => void;
  userProductIdOffer?: IWebOffer | IAuctionOffer;
  deletedOfferCallback: () => void;
  setDeletedOfferCallback: Dispatch<SetStateAction<() => void | null>>;
  topOffers: ITopOfferData[];
}

export const OffersContext = createContext<IOffersContext>(null);

export const OffersProvider = ({ children }: IOffersProvider) => {
  const { productTemplate, buyBarVariants } = useStoreSelector((state) => state.pdp);
  const {
    id: productTemplateId,
    isOfferable,
    isRaffleProduct,
    isResellable,
    productCategory,
    specialType,
    productType,
  } = productTemplate;
  const { availableSizes } = buyBarVariants;
  const { isAuthenticated } = useStoreSelector((state) => state.auth);
  const { logInBeforeAction } = useLogInBeforeAction();
  const [existingOffers, setExistingOffers] = useState<IWebOffer[] | IAuctionOffer[]>(null);
  const { selectedCountryCode } = useLocation();
  const { query } = useRouter();
  const [topOffers, setTopOffers] = useState<ITopOfferData[]>(null);
  const [isPlaceOfferModalShowing, setIsPlaceOfferModalShowing] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [placeOfferModalSize, setPlaceOfferModalSize] = useState(null);
  const [productVariant, setProductVariant] = useState<IVariantData>(null);
  const [userProductIdOffer, setUserProductIdOffer] = useState(null);
  const [isSnackbarVisible, setIsSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<ReactNode>(null);
  const [deletedOfferCallback, setDeletedOfferCallback] = useState<() => void | null>(null);
  const [offersLoginTestGroup, setOffersLoginTestGroup] = useState(null);

  const _isOfferable =
    isOfferable && !isSpecialType(specialType) && !isRaffleProduct && isResellable;

  const hidePlaceOfferModal = () => {
    setProductVariant(null);
    setUserProductIdOffer(null);
    setPlaceOfferModalSize(null);
    setIsPlaceOfferModalShowing(false);
  };

  const fetchProductIdAndUserOfferData = async (productId: string, countryCode: string) => {
    const [productIdData, existingProductIdOfferData] = await Promise.all([
      fetchProductIdData(productId, countryCode),
      fetchUserProductIdOffer(productId),
    ]);
    if (!isEmpty(existingProductIdOfferData)) {
      setUserProductIdOffer(existingProductIdOfferData);
    }
    if (!isEmpty(productIdData)) {
      setProductVariant(productIdData);
    }
  };

  const _showPlaceOfferModal: IOffersContext['showPlaceOfferModal'] = (
    size: number,
    selectedProductVariant?,
    userProductVariantOffer?,
    options?,
  ) => {
    setIsLoading(true);
    hidePlaceOfferModal();

    // On deleteOffer callback reset to null
    if (options?.onDeletedOfferCallbackFn) {
      setDeletedOfferCallback(() => options?.onDeletedOfferCallbackFn);
    } else {
      setDeletedOfferCallback(null);
    }
    try {
      // When productVariant has id we know is a unique product (like used, etc);
      if (selectedProductVariant?.id) {
        setProductVariant(selectedProductVariant);
      }

      if (userProductVariantOffer) {
        setUserProductIdOffer(userProductVariantOffer);
      }

      setPlaceOfferModalSize(size);
      setIsPlaceOfferModalShowing(true);
      // this callback is needed in case we want to perform
      // other action to update a different component
      // TODO: drawback of providers
      if (options?.onOpenModalCallbackFn) {
        options?.onOpenModalCallbackFn();
      }
    } catch (error) {
      bugsnag(error, { context: 'Offer provider: fetching user offer id' });
    } finally {
      setIsLoading(false);
    }
  };

  const showPlaceOfferModal: IOffersContext['showPlaceOfferModal'] = (
    size: number,
    selectedProductVariant?,
    userProductVariantOffer?,
    options?,
    offersLoginExperimentGroup?: string,
  ) => {
    if (!isAuthenticated) {
      setOffersLoginTestGroup(offersLoginExperimentGroup);

      if (
        offersLoginExperimentGroup &&
        offersLoginExperimentGroup === OFFERS_LOGIN_DARWIN_TEST.TEST
      ) {
        _showPlaceOfferModal(size, selectedProductVariant, userProductVariantOffer, options);
      } else {
        logInBeforeAction(() => {
          _showPlaceOfferModal(size, selectedProductVariant, userProductVariantOffer, options);
        });
      }
    } else {
      _showPlaceOfferModal(size, selectedProductVariant, userProductVariantOffer, options);
    }
  };

  const showSnackbarForOffer = (newExpirationDate: Date, offerAmount: string) => {
    const formattedNewExpirationDate = dayjs(newExpirationDate).format(localizeDate('MM/DD'));

    const newSnackbarMessage = (
      <Trans
        i18nKey={`${TP}.snackbarMessage`}
        defaults="You’ve updated <0>your offer</0> to {offerAmount}, expiring on {formattedNewExpirationDate}"
        components={[
          <OffersProvider.YourOfferCTA
            onClick={() => showPlaceOfferModal(placeOfferModalSize, productVariant)}
          />,
        ]}
        values={{ offerAmount, formattedNewExpirationDate }}
      />
    );

    setSnackbarMessage(newSnackbarMessage);
    setIsSnackbarVisible(true);
  };

  const getSizeInfo = () => {
    if (productVariant?.id) {
      return {
        ...productVariant,
        buyNowAmount: String(productVariant?.localizedPriceCents?.amount),
        buyNowAmountUsd: String(productVariant?.localizedPriceCents?.amountUsdCents),
        condition:
          productVariant?.shoeCondition === 'new'
            ? t(`${TP}.newWithDefects`, 'New with Defects')
            : t(`${TP}.used`, 'Used'),
        lastSaleAmount: null,
        lastSaleAmountUsd: null,
        sizePresentation: SIZE_MAP[placeOfferModalSize]
          ? capitalize(SIZE_MAP[placeOfferModalSize])
          : String(placeOfferModalSize),
        sizeNumerical: String(placeOfferModalSize),
      };
    }

    const sizeInfo = availableSizes.find(
      (_size) => _size?.sizeOption?.value === placeOfferModalSize,
    );
    const isSneaker = productCategory === 'shoes';

    return {
      ...sizeInfo,
      buyNowAmount: String(sizeInfo?.lowestPriceCents?.amount),
      buyNowAmountUsd: String(sizeInfo?.lowestPriceCents?.amountUsdCents),
      condition: isSneaker ? t(`${TP}.newInBox`, 'New in Box') : t(`${TP}.new`, 'New'),
      lastSaleAmount: String(sizeInfo?.lastSoldPriceCents?.amount),
      lastSaleAmountUsd: String(sizeInfo?.lastSoldPriceCents?.amountUsdCents),
      sizePresentation:
        sizeInfo?.sizeOption?.presentation ||
        (SIZE_MAP[placeOfferModalSize]
          ? capitalize(SIZE_MAP[placeOfferModalSize])
          : String(placeOfferModalSize)),
      sizeNumerical: String(placeOfferModalSize),
    };
  };

  // AKA fetchUserOffers
  const getExistingOffers = async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    try {
      const { offers } = await requestService({
        url: `/web-api/v1/offers/find?productTemplateId=${productTemplateId}`,
        method: 'get',
      });
      setExistingOffers(offers);
    } catch (error) {
      if (typeof error !== 'undefined') {
        bugsnag(error, { context: 'Fetching Existing Offers' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // AKA getUserOfferBySize
  const getExistingOffer = (sizeValue) => {
    return (existingOffers as Array<IWebOffer | IAuctionOffer>)?.find(
      (offer) => offer.size === sizeValue,
    );
  };

  const getExistingConditionalOffer = () => {
    if (productVariant?.id && userProductIdOffer) {
      return userProductIdOffer;
    }
  };

  const fetchTopOffers = async () => {
    if (!_isOfferable) return;
    setIsLoading(true);
    try {
      const data = await requestService({
        // highest_offers
        url: `/web-api/v1/highest_offers?productTemplateId=${productTemplateId}&countryCode=${selectedCountryCode}`,
        method: 'GET',
      });
      setTopOffers(data);
    } catch (error) {
      if (typeof error !== 'undefined') {
        bugsnag(error, { context: 'Highest Offers' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const { offerSize, productId } = query;

    if (offerSize) {
      if (productId) {
        try {
          void fetchProductIdAndUserOfferData(String(productId), selectedCountryCode);
          showPlaceOfferModal(Number(offerSize), productVariant);
        } catch (error) {
          if (typeof error !== 'undefined') {
            bugsnag(error, { context: 'Fetching productId and userOffer' });
          }
        }
      } else {
        showPlaceOfferModal(Number(offerSize));
      }
    }
  }, [query]);

  useEffect(() => {
    setExistingOffers(null);
  }, [productTemplateId]);

  const getTopOfferBySize = (size: number) => {
    const result = topOffers?.find((topOffer) => topOffer.size === size);
    return result;
  };

  const {
    buyNowAmount,
    buyNowAmountUsd,
    condition,
    lastSaleAmount,
    lastSaleAmountUsd,
    sizePresentation,
    sizeNumerical,
    boxCondition,
    shoeCondition,
  } = getSizeInfo();

  let buyNowCheckoutLink = [
    `/checkout?productTemplateId=${productTemplateId}`,
    `expectedPriceCents=${buyNowAmount}`,
    `instantShip=false`,
    `shoeCondition=${productVariant?.shoeCondition || shoeCondition}`,
    `size=${sizeNumerical}`,
  ].join('&');

  if (productType === 'sneakers') {
    buyNowCheckoutLink = `${buyNowCheckoutLink}&boxCondition=${
      productVariant?.boxCondition || boxCondition
    }`;
  }
  if (productVariant?.id) {
    buyNowCheckoutLink = `${buyNowCheckoutLink}&productId=${productVariant.id}`;
  }

  const values: IOffersContext = {
    buyNowAmount,
    buyNowAmountUsd,
    buyNowCheckoutLink,
    condition,
    deletedOfferCallback,
    existingOffers,
    fetchTopOffers,
    fetchUserProductIdOffer,
    getExistingConditionalOffer,
    getExistingOffer,
    getExistingOffers,
    getTopOfferBySize,
    hidePlaceOfferModal,
    isLoading,
    isOfferable: _isOfferable,
    isPlaceOfferModalShowing,
    isSnackbarVisible,
    lastSaleAmount,
    lastSaleAmountUsd,
    offersLoginTestGroup,
    placeOfferModalSize,
    productVariant,
    setDeletedOfferCallback,
    setIsSnackbarVisible,
    showPlaceOfferModal,
    showSnackbarForOffer,
    sizePresentation,
    snackbarMessage,
    topOffers,
    userProductIdOffer,
  };

  return <OffersContext.Provider value={values}>{children}</OffersContext.Provider>;
};

OffersProvider.YourOfferCTA = styled.span`
  text-decoration: underline;
  cursor: pointer;
`;
